import type { DataverseConnectionConfig } from "./types";

function normalizeApiBaseUrl(input: string): string {
  const trimmed = input.trim();
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

export class DataverseClient {
  private readonly baseUrl: string;
  private readonly bearerToken: string;

  constructor(connection: DataverseConnectionConfig) {
    this.baseUrl = normalizeApiBaseUrl(connection.baseUrl);
    this.bearerToken = connection.bearerToken.trim();
  }

  get normalizedBaseUrl(): string {
    return this.baseUrl;
  }

  resolveUrl(path: string): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  private async fetchWithDiagnosticsByUrl<T>(requestUrl: string): Promise<{
    data: T;
    status: number;
    statusText: string;
    requestUrl: string;
  }> {
    const response = await fetch(requestUrl, {
      headers: {
        Authorization: `Bearer ${this.bearerToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Dataverse request failed (${response.status}): ${text || response.statusText}`);
    }

    return {
      data: (await response.json()) as T,
      status: response.status,
      statusText: response.statusText,
      requestUrl,
    };
  }

  async fetchJson<T>(path: string): Promise<T> {
    const requestUrl = this.resolveUrl(path);
    const response = await fetch(requestUrl, {
      headers: {
        Authorization: `Bearer ${this.bearerToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Dataverse request failed (${response.status}): ${text || response.statusText}`);
    }

    return (await response.json()) as T;
  }

  async fetchJsonWithDiagnostics<T>(path: string): Promise<{
    data: T;
    status: number;
    statusText: string;
    requestUrl: string;
  }> {
    const requestUrl = this.resolveUrl(path);
    return this.fetchWithDiagnosticsByUrl<T>(requestUrl);
  }

  async fetchJsonUrlWithDiagnostics<T>(requestUrl: string): Promise<{
    data: T;
    status: number;
    statusText: string;
    requestUrl: string;
  }> {
    return this.fetchWithDiagnosticsByUrl<T>(requestUrl);
  }
}
